import {
	ArrowDropDown as ArrowDropDownIcon,
	ArrowDropUp as ArrowDropUpIcon,
	Assignment as AssignmentIcon,
	Close as CloseIcon,
	Commute as CommuteIcon,
	Dashboard as DashboardIcon,
	Logout as LogoutIcon,
	ManageAccounts as ManageAccountsIcon,
	Map as MapIcon,
	PeopleAlt as PeopleAltIcon,
	Settings as SettingsIcon
} from "@mui/icons-material";
import {
	AppBar,
	Avatar,
	Box,
	CircularProgress,
	CssBaseline,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Toolbar,
	Typography,
	useMediaQuery
} from "@mui/material";
import { GridMenuIcon } from "@mui/x-data-grid";
import React, { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import MMVDLogoSrc from "../../assets/logos/mmvd-logo.png";
import { ReactComponent as StellarviewLogo } from "../../assets/logos/ashoka-logo.svg";
import { ReactComponent as StellarviewSmallLogo } from "../../assets/logos/ashoka-small-logo.svg";
import { theme } from "../../assets/theme";
import ProfileDialog from "../../components/ProfileDialog";
import DownloadJobsDropdown from "../../components/DownloadJobsDropdown";
import TimeRangeSelection from "../../components/TimeRangeSelection";
import { useAppDispatch, useAppSelector } from "../../redux";
import { clearAuthState, logoutUser } from "../../redux/reducers/auth.reducer";
import { queries } from "../../redux/reducers/queries.reducer";
import authServices from "../../redux/services/auth.services";
import "./pageLayout.css";
import SessionExpiredDialog from "./SessionExpiredDialog";
import { useDownloadJobsManager } from "../../hooks/useDownloadJobsManager";

interface ISidebarItem {
	text: string;
	icon: React.ReactNode;
	path: string;
	children?: Omit<ISidebarItem, "children">[];
}

const DRAWER_WIDTH = 300;
const COLLAPSED_DRAWER_WIDTH = 80;

const TIME_FILTER_PATHS = ["/dashboard", "/violation-logs", "/reports"];

const PageLayout: FC = () => {
	const location = useLocation();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
	const authState = useAppSelector((state) => state.auth);
	const profileMenuAnchorRef = useRef<HTMLDivElement>(null);

	const [showLoader, setShowLoader] = useState<boolean>(true);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [expandedSidebarItem, setExpandedSidebarItem] = useState<string>("");
	const [profileMenuOpen, setProfileMenuOpen] = useState<boolean>(false);
	const [profileDialogOpen, setProfileDialogOpen] = useState<boolean>(false);

	const timeFilterEnabled = useMemo<boolean>(
		() => TIME_FILTER_PATHS.some((path) => location.pathname.startsWith(path)),
		[location.pathname]
	);

	// Initialize download jobs manager for background downloads.
	useDownloadJobsManager();

	const sidebarItemsList = useMemo<ISidebarItem[]>(() => {
		const sidebarItems: ISidebarItem[] = [];

		if (authState.user.permissions) {
			if (authState.user.permissions.dashboard.view) {
				sidebarItems.push({
					text: "Dashboard",
					icon: <DashboardIcon />,
					path: "/dashboard"
				});
			}

			if (authState.user.permissions.violation_logs.view) {
				sidebarItems.push({
					text: "Validation Logs",
					icon: <CommuteIcon />,
					path: "/violation-logs"
				});
			}

			if (authState.user.permissions.rto_metadata?.view) {
				sidebarItems.push({
					text: "Jurisdictions & RTOs",
					icon: <MapIcon />,
					path: "/jurisdictions"
				});
			}

			if (authState.user.permissions.user_management.view) {
				sidebarItems.push({
					text: "Roles & Permissions",
					icon: <ManageAccountsIcon />,
					path: "/roles-permissions"
				});
			}

			if (
				authState.user.permissions.user_management.view ||
				authState.user.permissions.user_activity_logs.view ||
				authState.user.permissions.user_session_management.view
			) {
				sidebarItems.push({
					text: "User Management",
					icon: <PeopleAltIcon />,
					path: "/user-management"
				});
			}

			if (authState.user.permissions.dashboard.view) {
				sidebarItems.push({
					text: "Reports",
					icon: <AssignmentIcon />,
					path: "/reports"
				});
			}

			sidebarItems.push({
				text: "Configuration",
				icon: <SettingsIcon />,
				path: "/configuration"
			});
		}

		return sidebarItems;
	}, [authState.user.permissions]);

	function openDrawer() {
		setDrawerOpen(true);
	}

	function closeDrawer() {
		setDrawerOpen(false);
	}

	function expandSidebarDropdown(path: string) {
		setExpandedSidebarItem(path);
		if (isMobileScreen) closeDrawer();
	}

	function navigateToPath(path: string) {
		navigate(path);
		expandSidebarDropdown(path);
	}

	function openProfileMenu() {
		setProfileMenuOpen(true);
	}

	function closeProfileMenu() {
		setProfileMenuOpen(false);
	}

	function openProfileDialog() {
		setProfileDialogOpen(true);
		setProfileMenuOpen(false);
	}

	function closeProfileDialog() {
		setProfileDialogOpen(false);
	}

	function logout() {
		dispatch(logoutUser());
	}

	function checkLoginToken() {
		authServices
			.handleCheckLoginToken()
			.then((response) => {
				if (response === "token_valid") setShowLoader(false);
				else if (response === "token_invalid") dispatch(clearAuthState());
			})
			.catch((error) => {
				if (Number(error) === 401) dispatch(clearAuthState());
			});
	}

	useEffect(() => {
		if (!authState.user.token || !authState.otpVerified) {
			if (authState.defaultPasswordError || authState.resetPasswordError) {
				navigate("/change-password");
			} else {
				navigate("/login");
			}
			dispatch(queries.util.resetApiState());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState.user.token]);

	useEffect(() => {
		checkLoginToken();

		const activeScreen = sidebarItemsList.find((item) => location.pathname.startsWith(item.path));
		if (activeScreen && activeScreen.children) {
			setExpandedSidebarItem(activeScreen.path);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const drawer = (
		<Box className="page-layout-drawer-wrapper">
			<Box className="mobile-drawer-close-icon">
				<IconButton sx={{ color: "#616161" }} size="small" onClick={closeDrawer}>
					<CloseIcon color="inherit" />
				</IconButton>
			</Box>

			<Toolbar classes={{ root: "page-layout-toolbar" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", paddingRight: "0.3rem" }}>
					{/* <EdgeTrackSmallLogo height="56px" fill="white" /> */}
					<img src={MMVDLogoSrc} alt="MMVD" height="56px" />
					<Box>
						<Typography
							fontFamily="Patua One"
							fontWeight={500}
							fontSize="22px"
							whiteSpace="nowrap"
							sx={{ opacity: drawerOpen ? 1 : 0 }}
						>
							MTES
						</Typography>

						<Typography
							variant="body2"
							fontFamily="Patua One"
							fontSize="12px"
							whiteSpace="nowrap"
							sx={{ opacity: drawerOpen ? 1 : 0 }}
						>
							Mobile Traffic Enforcement System
						</Typography>
					</Box>
				</Box>
			</Toolbar>

			<Divider sx={{ borderColor: "#C5C1EA33", width: "80%" }} />

			<List sx={{ paddingX: "0.75rem", width: "100%" }}>
				{sidebarItemsList.map((item, index) => (
					<Fragment key={index}>
						<ListItem
							key={index}
							disablePadding
							classes={{
								root: `sidebar-list-item ${location.pathname.startsWith(item.path) ? "active" : ""} ${
									!drawerOpen ? "collapsed" : ""
								}`
							}}
							sx={{ color: location.pathname.startsWith(item.path) ? "white" : "#424242" }}
						>
							<ListItemButton onClick={() => navigateToPath(item.path)}>
								<ListItemIcon
									sx={{
										color: location.pathname.startsWith(item.path) ? "white" : "#424242",
										minWidth: "unset",
										marginY: "4px"
									}}
								>
									{item.icon}
								</ListItemIcon>

								<ListItemText
									primary={item.text}
									classes={{ primary: "sidebar-list-item-text" }}
									sx={{ opacity: drawerOpen ? 1 : 0, whiteSpace: "nowrap" }}
								/>

								{item.children ? (
									expandedSidebarItem === item.path ? (
										<ArrowDropUpIcon sx={{ color: location.pathname.startsWith(item.path) ? "white" : "#424242" }} />
									) : (
										<ArrowDropDownIcon sx={{ color: "#616161" }} />
									)
								) : null}
							</ListItemButton>
						</ListItem>
					</Fragment>
				))}
			</List>

			<Box className={`sidebar-bottom-logo-wrapper ${drawerOpen ? "extra-padding" : ""}`}>
				<StellarviewSmallLogo
					width={drawerOpen ? "0px" : "90%"}
					style={{ visibility: drawerOpen ? "hidden" : "visible", transition: "width 0.02s ease-out 0.1s" }}
				/>

				<StellarviewLogo
					width={drawerOpen ? "90%" : "0px"}
					style={{ visibility: drawerOpen ? "visible" : "hidden", transition: "width 0.02s ease-out 0.1s" }}
				/>
			</Box>
		</Box>
	);

	return (
		<>
			{showLoader ? (
				<Box className="screen-loader-wrapper">
					<CircularProgress />
				</Box>
			) : (
				<Box sx={{ display: "flex" }}>
					<CssBaseline />

					<Box component="nav" sx={{ width: { lg: COLLAPSED_DRAWER_WIDTH }, flexShrink: { lg: 0 } }}>
						<Drawer
							variant="temporary"
							open={drawerOpen}
							onClose={closeDrawer}
							ModalProps={{ keepMounted: true }}
							sx={{ display: { xs: "block", lg: "none" } }}
							PaperProps={{ elevation: 0, sx: { backgroundColor: "transparent", width: DRAWER_WIDTH } }}
							classes={{ paper: "mobile-drawer-paper" }}
						>
							{drawer}
						</Drawer>

						<Drawer
							variant="permanent"
							sx={{ display: { xs: "none", lg: "block" } }}
							PaperProps={{
								elevation: 0,
								sx: {
									backgroundColor: "transparent",
									width: drawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH
								}
							}}
							onMouseEnter={openDrawer}
							onMouseLeave={closeDrawer}
							classes={{ paper: "desktop-drawer-paper" }}
						>
							{drawer}
						</Drawer>
					</Box>

					<Box className={`desktop-drawer-background-overlay ${drawerOpen ? "visible" : ""}`} />

					<Box
						className="screen-content-wrapper"
						sx={{ width: { xs: "100%", lg: `calc(100vw - ${COLLAPSED_DRAWER_WIDTH}px)` } }}
					>
						<AppBar
							sx={{
								backgroundColor: "#FEFEFE",
								padding: "0.5rem 0.5rem 0.5rem 0"
							}}
							position="relative"
							elevation={0}
						>
							<Box className="appbar-content-wrapper">
								<IconButton
									sx={{ color: "#616161", display: { xs: "block", lg: "none" } }}
									size="small"
									onClick={openDrawer}
								>
									<GridMenuIcon color="inherit" />
								</IconButton>

								<Box className="header-actions-wrapper">
									{timeFilterEnabled ? <TimeRangeSelection customOnly /> : null}

										<DownloadJobsDropdown />

									<Box
										component="div"
										className="header-avatar-wrapper"
										id="profile-button"
										aria-controls="profile-menu"
										aria-haspopup
										aria-expanded={profileMenuOpen}
										onClick={openProfileMenu}
										ref={profileMenuAnchorRef}
									>
										<Avatar classes={{ root: "profile-avatar-root" }}>
											{(authState.user.full_name ?? "")
												.split(" ")
												.map((item) => item.slice(0, 1))
												.join("")}
										</Avatar>
									</Box>

									<Menu
										id="profile-menu"
										anchorEl={profileMenuAnchorRef.current}
										open={profileMenuOpen}
										onClose={closeProfileMenu}
										MenuListProps={{ "aria-labelledby": "profile-button" }}
										disableAutoFocusItem
										transformOrigin={{ horizontal: "right", vertical: "top" }}
										anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
										classes={{ paper: "profile-menu-paper" }}
									>
										<MenuItem disableRipple onClick={openProfileDialog} classes={{ root: "profile-menu-item-root" }}>
											<Box sx={{ display: "flex", alignItems: "center", gap: "12px", width: "100%" }}>
												<Avatar classes={{ root: "profile-menu-avatar-root" }}>
													{(authState.user.full_name ?? "")
														.split(" ")
														.map((item) => item.slice(0, 1))
														.join("")}
												</Avatar>

												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Typography
														variant="subtitle2"
														fontWeight={600}
														noWrap
														sx={{ fontSize: "15px", lineHeight: 1.3 }}
													>
														{authState.user.full_name}
													</Typography>

													<Typography
														variant="body2"
														color="text.secondary"
														noWrap
														sx={{ fontSize: "13px", lineHeight: 1.3, mt: 0.5 }}
													>
														{authState.user.email}
													</Typography>
												</Box>
											</Box>
										</MenuItem>

										<Divider sx={{ mx: "0.5rem", borderColor: "rgba(0, 0, 0, 0.08)" }} />

										<MenuItem disableRipple onClick={logout} classes={{ root: "profile-menu-logout-item-root" }}>
											<ListItemIcon sx={{ minWidth: "40px" }}>
												<LogoutIcon fontSize="small" sx={{ color: "#f44336" }} />
											</ListItemIcon>

											<ListItemText>
												<Typography variant="body2" sx={{ fontWeight: 500, color: "#f44336" }}>
													Logout
												</Typography>
											</ListItemText>
										</MenuItem>
									</Menu>
								</Box>
							</Box>
						</AppBar>

						<Box component="main" className="screen-main-component">
							<Box className="screen-outlet-wrapper">
								<Outlet />
							</Box>
						</Box>
					</Box>

					<SessionExpiredDialog />

					<ProfileDialog open={profileDialogOpen} onClose={closeProfileDialog} />
				</Box>
			)}
		</>
	);
};

export default PageLayout;
